import { ReactNode } from "react";
import { EntityType } from "@common/Common.types";
import { FilterOperatorUi } from "@common/api/filterTypes";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { NumberIntervalValue } from "@common/components/filterPopover/subMenues/NumberFieldSubMenu";
import { RiskLevelIndicator } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { RiskAssessmentStatus } from "../RiskAssessmentStatus";
import { AssessmentConfidence } from "../AssessmentConfidence";
import { RiskMitigationPlanStatus } from "../riskMitigationStatus";
import { RiskTreatmentType } from "../riskTreatmentType";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import { RiskExposureSummary } from "../riskOperationalOverview/RiskOperationalOverview.types";
import { FrameworkControlStatus } from "../../framework/frameworks/Frameworks.types";

// A scenario is assessed against a specific RiskAreaRevision, so it carries its own scale + labels. risk and
// target are levels on that scenario's own scale (1..scale), used to derive the chip colors.
export interface RiskScenarioItem {
    id: string;
    // The scenario-definition id (distinct from id, which is the assessment id). Used to deep-link into the
    // mitigation overview pre-filtered by this scenario.
    riskScenarioId: string;
    status: RiskAssessmentStatus;
    name: string;
    scale: number;
    consequence: number | null;
    probability: number | null;
    consequenceLabel: string;
    probabilityLabel: string;
    risk: number | null;
    riskLabel: string;
    target: number | null;
    targetLabel: string | null;
    assessedAt: string | null;
    assessedByName: string | null;
    participantNames: Array<string>;
    // The current revision's stamped exposure and the active plan's stamped expected exposure — both already
    // gated by the backend to the current global exposure configuration, null otherwise.
    exposureValue: number | null;
    planExpectedExposureValue: number | null;
    // The active mitigation plan's stamped estimated cost (always money), gated by the backend to the current
    // exposure configuration; null when there is no active plan or it carries no stamped cost.
    planEstimatedCost: number | null;
    // The treatment decision axis. mitigationPlanStatus is the Mitigate sub-state — only non-null when
    // treatmentType === Mitigate.
    treatmentType: RiskTreatmentType;
    mitigationPlanStatus: RiskMitigationPlanStatus | null;
    // The active treatment was authored against an older revision than the current one. For an accepted risk,
    // reconfirm is disallowed (a fresh, signed-off acceptance is required) when either flag is set:
    // acceptanceWorsened — the level rose on the same matrix; acceptanceScaleChanged — the scale/matrix itself
    // changed, making the old and new levels incomparable.
    isTreatmentDrifted: boolean;
    acceptanceWorsened: boolean;
    acceptanceScaleChanged: boolean;
    // A completed mitigation plan (still current, not yet promoted) whose anchor differs from the current
    // revision — the risk was reassessed/reconfirmed after completion, so the mitigation is stale. Drives the
    // overview triangle and the "create a new mitigation" prompt. Reuses driftAnchor* for its date facts.
    isCompletedMitigationStale: boolean;
    // The level + date the active treatment was anchored against (acceptance level/date, or the plan's anchor
    // level + creation date). Populated only for a drifted Accept/Mitigate; null otherwise. Paired with the
    // current risk/riskLabel + assessedAt to spell out the cross-revision drift in the warning tooltip.
    driftAnchorRiskLabel: string | null;
    driftAnchorDate: string | null;
    // Level names on the scenario's own scale (index 0 = level 1), used to offer expected-after-mitigation levels.
    consequenceLevels: Array<string>;
    probabilityLevels: Array<string>;
    // The scenario's frozen risk-level indicators (index 0 = level 1), driving the risk/target chip visuals.
    riskLevelIndicators: Array<RiskLevelIndicator>;
    // The framework controls linked to this assessment and their rolled-up status (any Overdue wins). Empty +
    // null when no framework is in scope.
    linkedControlIds: Array<string>;
    linkedControlStatus: FrameworkControlStatus | null;
    // The number of documents linked to this assessment, surfaced as the opt-in Documentation column.
    documentationCount: number;
    note: string | null;
    // The current revision's stated confidence; null when unscored or scored without one.
    confidence: AssessmentConfidence | null;
}

// The entity's worst-case current risk across its scored scenarios, computed by the backend.
export interface HighestRisk {
    risk: number;
    riskLabel: string;
    scale: number;
}

// What a set of scenario assessments is about: one assessed entity, or the organisation as a whole. Mirrors the
// backend, where an assessment with no entity has the organisation as its subject.
export type RiskAssessmentSubject = { kind: "entity"; entityType: EntityType; entityId: string } | { kind: "organisation" };

export interface RiskAssessmentOverviewResult {
    // Both null when the organisation as a whole is the subject; name is then empty.
    type: EntityType | null;
    entityId: string | null;
    name: string;
    description: string | null;
    responsibleId: string | null;
    tagIds: Array<string>;
    businessAreaIds: Array<string>;
    scenarios: Array<RiskScenarioItem>;
    highestRisk: HighestRisk | null;
    // The highest current risk among the entity's one-hop graph neighbours, labelled on the area's current config
    // exactly like the operational overview's Linked risk column. Null when no neighbour carries one.
    linkedRisk: HighestRisk | null;
    // The area's current-config risk appetite. A scenario whose risk level exceeds it is "over appetite".
    appetite: number;
    // The area's current-config risk-level indicators (index 0 = level 1), driving the highest-risk summary chip.
    riskLevelIndicators: Array<RiskLevelIndicator>;
    // The entity's exposure roll-up for the header tile; null when exposure is not configured (None mode).
    exposure: RiskExposureSummary | null;
    exposureMode: RiskExposureMode;
    exposureCurrencyCode: string | null;
    exposureScaleUpperBound: number | null;
}

// Column ids persisted in saved views; must match RiskAssessmentsOverviewColumns in the backend strategy.
export enum RiskAssessmentColumn {
    Scenario = "scenario",
    Status = "status",
    Consequence = "consequence",
    Probability = "probability",
    Risk = "risk",
    AssessmentDate = "assessmentDate",
    AssessedBy = "assessedBy",
    Participants = "participants",
    Treatment = "treatment",
    Mitigation = "mitigation",
    ControlCount = "controlCount",
    ControlStatus = "controlStatus",
    Documentation = "documentation",
    EstimatedLoss = "estimatedLoss",
    EstimatedCost = "estimatedCost",
    ExposureAfterMitigation = "exposureAfterMitigation",
    Note = "note",
    Confidence = "confidence",
}

export const allRiskAssessmentColumns: Array<RiskAssessmentColumn> = Object.values(RiskAssessmentColumn);

export interface RiskAssessmentExportHeader {
    header: string;
    column: string;
}

// One export row per displayed scenario, in on-screen order. Filtering/sorting is client-side, so the frontend
// supplies both the order and the already-resolved control-status rollup; the backend re-fetches the rest.
export interface RiskAssessmentExportRow {
    assessmentId: string;
    controlStatus: FrameworkControlStatus | null;
}

export interface RiskAssessmentExportRequest {
    riskAreaId: string;
    // Both null when the organisation as a whole is the subject.
    entityType: EntityType | null;
    entityId: string | null;
    rows: Array<RiskAssessmentExportRow>;
}

// The client-side filter snapshot persisted in saved views. Cleared filters are dropped from the maps (not
// stored as empty values) so the shape deep-equals a saved view's JSON round-trip. Sort lives top-level as
// sortByColumn/sortDirection because the shared change detection in PageView.hooks excludes those keys —
// sorting should not flag a view as changed, same as the other saved-view pages.
export interface RiskAssessmentsSavedFilter {
    selectFilters: Record<string, Array<string>>;
    dateFilters: Record<string, DateFieldValue>;
    numberFilters: Record<string, NumberIntervalValue>;
    filterOperators: Record<string, FilterOperatorUi>;
    sortByColumn?: string;
    sortDirection?: "asc" | "desc";
}

// Presentation row: the scenario plus the derived treatment label. Risk colors are NOT stored — they are a pure
// function of the risk level and the scenario's own scale, derived at render time.
export interface RiskScenarioRow extends RiskScenarioItem {
    treatmentLabel: string;
    mitigationStatusLabel: string;
    statusLabel: string;
    assessmentDateLabel: string;
    assessedByName: string;
    participantNamesLabel: string;
    driftWarningText: ReactNode;
    staleWarningText: ReactNode;
    // The note is rich-text HTML, so the Note column sorts and searches this text instead of the markup.
    notePlainText: string;
    // Empty when the current revision states no confidence.
    confidenceLabel: string;
}
